import * as React from "react";
import { useLayoutEffect } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { DateFilter, DropdownValue } from "../DateFilter";
import { Loader } from "../Loader";
import moment from "moment";
import { injectIntl, useIntl } from "react-intl";
import { TranslateText } from '../TranslateText';
import { HintMessage } from '../HintMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const randomID = (Math.random() + 1).toString(36).substring(7);
const apiDateFormat = 'YYYY-MM-DD H:mm';
const scrollbarConfig = {
  default: 0,
  value: 30,
  gripSize: 25,
  opacity: 1,
  strokeOpacity: 0.8,
  saturation: 0.4,
  alignCenter: "center",
  alignMiddle: "middle",
  dateFormatter: "yyyy-MM-dd HH:mm:ss"
};
const yAxisConfig = {
  default: 0,
  fontSize: 12,
  fontWeight: "400",
  strokeOpacity: 0.5,
  cellStartLocation: -0.1,
  cellEndLocation: 1.1,
  minGridDistance: 10,
  sOpacity: 1,
  strokeWidth: 0.7
};
const lineSeriesConfig = {
  default: 0,
  fillOpacity: 1,
  size: 15,
  imageDx: 10
};
const chartConstant = {
  dateFilter: {
    [DropdownValue.Day]: {
      count: 10,
      timeUnit: "hours",
      toolTipDateFormat: "hh:mm a",
      dateFormat: "MM/dd/yyyy, hh:mm:ss a",
      setKeys: "hour" + "h:mm a",
      tooltipLocation: 10,
      xAxisTimeFormat: "h:mm A",
      minGridDistance: 70
    },
    [DropdownValue.Week]: {
      count: 1,
      timeUnit: "time",
      toolTipDateFormat: "dt HH:mm a",
      dateFormat: "MM/dd/yyyy, hh:mm:ss a",
      setKeys: "hour" + "MMM dd h:mm a",
      periodChangeDateFormats: "hour" + "MMM dd h:mm a",
      tooltipLocation: 10,
      xAxisDateFormat: "Do",
      xAxisTimeFormat: "h:mm A",
      minGridDistance: 40
    },
    [DropdownValue.Month]: {
      count: 1,
      timeUnit: "time",
      toolTipDateFormat: "dd MMM",
      dateFormat: "MM/dd/yyyy, hh:mm a",
      setKeys: "month" + "MMM",
      periodChangeDateFormats: "month" + "MMM",
      xAxisDateFormat: "MMM DD",
      minGridDistance: 70
    },
    [DropdownValue.Quarter]: {
      count: 1,
      timeUnit: "week",
      toolTipDateFormat: "MMM",
      dateFormat: "MM/dd/yyyy, hh:mm a",
      setKeys: "hour" + "MMM dd h:mm a",
      periodChangeDateFormats: "hour" + "MMM dd h:mm a",
      xAxisDateFormat: "MMM DD",
      minGridDistance: 30
    },
    [DropdownValue.Year]: {
      count: 1,
      timeUnit: "month",
      toolTipDateFormat: "MMM yyyy",
      dateFormat: "MM/dd/yyyy, hh:mm a",
      setKeys: "year" + "yyyy",
      periodChangeDateFormats: "year" + "yyyy",
      xAxisDateFormat: "MMM",
      xAxisTimeFormat: "YYYY",
      minGridDistance: 20
    },
    [DropdownValue.Custom]: {
      count: 1,
      timeUnit: "month",
      toolTipDateFormat: "MMM yyyy",
      dateFormat: "MM/dd/yyyy, hh:mm a",
      setKeys: "year" + "yyyy",
      periodChangeDateFormats: "year" + "yyyy",
      xAxisDateFormat: "MMM",
      xAxisTimeFormat: "YYYY",
      minGridDistance: 20
    }
  }
};
const StackBarChart = ({
  value,
  slider,
  stack,
  filterValue
}) => {
  if (!filterValue.fromDate || !filterValue.toDate) {
    return /*#__PURE__*/_jsx("div", {});
  }
  const intl = useIntl();
  const bodyStyles = window.getComputedStyle(document.body);
  const defaultColor = bodyStyles.getPropertyValue("--kc-txt-color");
  const scrollGripColor = bodyStyles.getPropertyValue("--kc-chart-scrollbar-grip-color");
  const scrollGripColorBg = bodyStyles.getPropertyValue("--kc-chart-scrollbar-bg-color");
  function getXAxisByFilter(filterValue, dateAxis) {
    let xAxis = dateAxis;
    xAxis.min = new Date(filterValue.fromDate).getTime();
    xAxis.max = new Date(filterValue.toDate).getTime();
    switch (filterValue.value) {
      case DropdownValue.Day:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Day].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Day].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Day].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Day].toolTipDateFormat;
        xAxis.renderer.line.stroke = am4core.color(defaultColor);
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Day].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Day].setKeys);
        xAxis.renderer.tooltipLocation = chartConstant.dateFilter[DropdownValue.Day].tooltipLocation;
        xAxis.renderer.labels.template.adapter.add("text", (_, target) => {
          if (target?.dataItem?.date) {
            let h = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Day].xAxisTimeFormat);
            return h;
          }
        });
        break;
      case DropdownValue.Week:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Week].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Week].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Week].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Week].toolTipDateFormat;
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Week].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Week].setKeys);
        xAxis.periodChangeDateFormats.setKey(chartConstant.dateFilter[DropdownValue.Week].periodChangeDateFormats);
        xAxis.renderer.labels.template.adapter.add("text", (_, target) => {
          if (target?.dataItem?.date) {
            let md = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Week].xAxisDateFormat);
            let h = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Week].xAxisTimeFormat);
            return md + "\n" + h;
          }
        });
        break;
      case DropdownValue.Month:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Month].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Month].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Month].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Month].toolTipDateFormat;
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Month].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Month].setKeys);
        xAxis.periodChangeDateFormats.setKey(chartConstant.dateFilter[DropdownValue.Month].periodChangeDateFormats);
        xAxis.renderer.labels.template.adapter.add("text", (_, target) => {
          if (target?.dataItem?.date) {
            let md = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Month].xAxisDateFormat);
            return md;
          }
        });
        break;
      case DropdownValue.Quarter:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Quarter].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Quarter].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Quarter].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Quarter].toolTipDateFormat;
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Quarter].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Quarter].setKeys);
        xAxis.periodChangeDateFormats.setKey(chartConstant.dateFilter[DropdownValue.Quarter].periodChangeDateFormats);
        break;
      case DropdownValue.Year:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Year].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Year].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Year].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Year].toolTipDateFormat;
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Year].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Year].setKeys);
        xAxis.periodChangeDateFormats.setKey(chartConstant.dateFilter[DropdownValue.Year].periodChangeDateFormats);
        xAxis.renderer.labels.template.adapter.add("text", (_, target) => {
          if (target?.dataItem?.date) {
            let md = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Year].xAxisDateFormat);
            let h = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Year].xAxisTimeFormat);
            return md + "\n" + h;
          }
        });
        break;
      case DropdownValue.Custom:
        xAxis.renderer.minGridDistance = chartConstant.dateFilter[DropdownValue.Custom].minGridDistance;
        xAxis.baseInterval = {
          timeUnit: chartConstant.dateFilter[DropdownValue.Custom].timeUnit,
          count: chartConstant.dateFilter[DropdownValue.Custom].count
        };
        xAxis.tooltipDateFormat = chartConstant.dateFilter[DropdownValue.Custom].toolTipDateFormat;
        xAxis.dateFormatter.dateFormat = chartConstant.dateFilter[DropdownValue.Custom].dateFormat;
        xAxis.dateFormats.setKey(chartConstant.dateFilter[DropdownValue.Custom].setKeys);
        xAxis.periodChangeDateFormats.setKey(chartConstant.dateFilter[DropdownValue.Custom].periodChangeDateFormats);
        xAxis.renderer.labels.template.adapter.add("text", (_, target) => {
          if (target?.dataItem?.date) {
            let md = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Custom].xAxisDateFormat);
            let h = moment(new Date(target.dataItem.date)).format(chartConstant.dateFilter[DropdownValue.Custom].xAxisTimeFormat);
            return md + "\n" + h;
          }
        });
        break;
      default:
        break;
    }
    return xAxis;
  }
  var filterType = "";
  if (filterValue.hasOwnProperty("value")) {
    filterType = filterValue.value;
  }
  useLayoutEffect(() => {
    // Themes begin
    am4core.useTheme(am4themes_animated);
    // Themes end
    let chart = am4core.create(randomID, am4charts.XYChart);
    if (chart.logo) {
      chart.logo.disabled = true;
    }
    chart.scrollbarX = new am4core.Scrollbar();
    chart.scrollbarX.marginLeft = scrollbarConfig.default;
    chart.scrollbarX.marginBottom = scrollbarConfig.value;
    function customizeGrip(grip) {
      grip.showSystemTooltip = false;
      grip.icon.disabled = true;
      grip.background.disabled = true;
      grip.background.strokeOpacity = scrollbarConfig.default;
      let img = grip.createChild(am4core.Circle);
      img.width = scrollbarConfig.gripSize;
      img.height = scrollbarConfig.gripSize;
      img.fill = am4core.color(scrollGripColor);
      img.align = scrollbarConfig.alignCenter;
      img.valign = scrollbarConfig.alignMiddle;
    }
    chart.scrollbarX.thumb.showSystemTooltip = false;
    chart.scrollbarX.thumb.background.fill = am4core.color(scrollGripColorBg);
    chart.scrollbarX.thumb.background.fillOpacity = scrollbarConfig.opacity;
    chart.scrollbarX.thumb.background.stroke = am4core.color(scrollGripColorBg);
    chart.scrollbarX.thumb.background.strokeWidth = scrollbarConfig.default;
    chart.scrollbarX.thumb.background.strokeOpacity = scrollbarConfig.strokeOpacity;

    // below config for scrollbar grip hover color
    let hoverState = chart.scrollbarX.thumb.background.states.create("hover");
    let hoverDown = chart.scrollbarX.thumb.background.states.create("down");
    hoverState.properties.fill = am4core.color(scrollGripColorBg);
    hoverDown.properties.fill = am4core.color(scrollGripColorBg);
    customizeGrip(chart.scrollbarX.startGrip);
    customizeGrip(chart.scrollbarX.endGrip);
    chart.scrollbarX.startGrip.disabled = !slider;
    chart.scrollbarX.endGrip.disabled = !slider;
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "zoomX";
    chart.hiddenState.properties.opacity = scrollbarConfig.opacity;
    chart.paddingRight = scrollbarConfig.value;
    chart.dateFormatter.inputDateFormat = scrollbarConfig.dateFormatter;
    var colorSet = new am4core.ColorSet();
    colorSet.saturation = scrollbarConfig.saturation;
    let chartValue = [];
    value?.forEach(item => {
      const stackItem = stack.find(stackItem => stackItem.value === item.stack);
      const chartProperty = {
        name: "",
        fromDate: "",
        toDate: "",
        color: "",
        task: "",
        formattedTask: "",
        value: "",
        image: "",
        message: ""
      };
      chartProperty.name = item.y;
      chartProperty.fromDate = item.x.from;
      chartProperty.toDate = item.x.to;
      chartProperty.color = stackItem?.color;
      chartProperty.task = stackItem?.label;
      chartProperty.message = item.message;
      chartProperty.image = item.image;
      chartProperty.formattedTask = stackItem?.label ? intl.formatMessage({
        id: stackItem?.label
      }) : '';
      chartValue.push(chartProperty);
    });
    chart.data = chartValue;
    let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.renderer.labels.template.fill = am4core.color(defaultColor);
    categoryAxis.renderer.labels.template.fontSize = yAxisConfig.fontSize;
    categoryAxis.renderer.line.strokeOpacity = yAxisConfig.strokeOpacity;
    categoryAxis.renderer.line.strokeWidth = yAxisConfig.strokeOpacity;
    categoryAxis.renderer.line.stroke = am4core.color(defaultColor);
    categoryAxis.dataFields.category = "name";
    categoryAxis.renderer.grid.template.location = yAxisConfig.default;
    categoryAxis.renderer.inversed = true;
    categoryAxis.renderer.cellStartLocation = yAxisConfig.cellStartLocation;
    categoryAxis.renderer.cellEndLocation = yAxisConfig.cellEndLocation;
    categoryAxis.renderer.minGridDistance = yAxisConfig.minGridDistance;

    //background grid lines for y axes
    categoryAxis.renderer.grid.template.strokeOpacity = yAxisConfig.sOpacity;
    categoryAxis.renderer.grid.template.stroke = am4core.color(defaultColor);
    categoryAxis.renderer.grid.template.strokeWidth = yAxisConfig.strokeWidth;

    //set up x-axis
    var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.labels.template.fill = am4core.color(defaultColor);
    dateAxis = getXAxisByFilter(filterValue, dateAxis);
    dateAxis.renderer.labels.template.fontSize = yAxisConfig.fontSize;
    dateAxis.fontWeight = "400";
    dateAxis.renderer.labels.template.fill = am4core.color(defaultColor);

    //y axes information
    let series1 = chart.series.push(new am4charts.ColumnSeries());
    series1.dataFields.openDateX = "fromDate";
    series1.dataFields.dateX = "toDate";
    series1.name = "idle";
    series1.dataFields.categoryY = "name";
    let withErrorTooltip = "{message}";
    let displayToolTip = "{name} - {formattedTask}  : {openDateX}[/] - {dateX}[/]";
    chartValue?.forEach(item => {
      if (item.name === "") {
        item.task = item.task ? item.task.toLowerCase().split(" ").map(s => s.charAt(0).toUpperCase() + s.substring(1)).join(" ") : '';
        series1.columns.template.tooltipText = withErrorTooltip;
      }
      series1.columns.template.tooltipText = displayToolTip;
    });
    series1.columns.template.propertyFields.fill = "color"; // get color from data - stack bar colors
    series1.columns.template.propertyFields.strokeOpacity = "strokeOpacity";
    series1.columns.template.stroke = am4core.color(defaultColor);
    series1.columns.template.fillOpacity = lineSeriesConfig.fillOpacity;
    let lineSeries = chart.series.push(new am4charts.LineSeries());
    lineSeries.dataFields.categoryY = "name";
    lineSeries.dataFields.dateX = "fromDate";
    lineSeries.name = "";
    lineSeries.strokeWidth = lineSeriesConfig.default;
    let bullet = lineSeries.bullets.push(new am4charts.Bullet());
    let image = bullet.createChild(am4core.Image);
    image.width = lineSeriesConfig.size;
    image.height = lineSeriesConfig.size;
    image.horizontalCenter = "middle";
    image.verticalCenter = "middle";
    image.propertyFields.href = "image";
    chart.data?.forEach(data => {
      if (data.name === "") {
        image.tooltipText = withErrorTooltip;
      }
    });
    if (filterType === "Day") {
      image.dx = lineSeriesConfig.imageDx;
    }
    return () => {
      chart.dispose();
    };
  }, [filterValue, value]);
  return /*#__PURE__*/_jsx("div", {
    className: "kc-ui-chart",
    id: randomID
  });
};
const _HorizontalStackBarChart = props => {
  const {
    title,
    value,
    stack,
    dateFilterTitle,
    dateFilter,
    dateFilterOptions,
    dateFilterValue,
    onTriggerDateFilterChange,
    dateFilterDateFormat,
    legend,
    loader,
    slider,
    width,
    disabled,
    noValueText,
    showHintMessage,
    hintMessage
  } = props;
  const [dateValue, setDateValue] = React.useState(dateFilterValue);
  const [chartValue, setChartValue] = React.useState(value);
  const [stackValue, setStackValue] = React.useState(stack);
  React.useEffect(() => {
    setChartValue(value);
    setStackValue(stack);
  }, [value, stack]);

  //let _event = dateFilterValue;
  const dateChange = e => {
    const dateFilterValue = e.data;
    let formattedDate = e.data;
    switch (dateFilterValue.value) {
      case DropdownValue.Week:
        formattedDate = {
          value: dateFilterValue.value,
          fromDate: moment(dateFilterValue.toDate).subtract(1, 'week').startOf('day').format(apiDateFormat),
          toDate: dateFilterValue.toDate
        };
        break;
      case DropdownValue.Month:
        formattedDate = {
          value: dateFilterValue.value,
          fromDate: moment(dateFilterValue.toDate).subtract(1, 'month').startOf('day').format(apiDateFormat),
          toDate: dateFilterValue.toDate
        };
        break;
      case DropdownValue.Quarter:
        formattedDate = {
          value: dateFilterValue.value,
          fromDate: moment(dateFilterValue.toDate).subtract(1, 'quarter').startOf('day').format(apiDateFormat),
          toDate: dateFilterValue.toDate
        };
        break;
      case DropdownValue.Year:
        formattedDate = {
          value: dateFilterValue.value,
          fromDate: moment(dateFilterValue.toDate).subtract(1, 'year').startOf('day').format(apiDateFormat),
          toDate: dateFilterValue.toDate
        };
        break;
      default:
        formattedDate = {
          value: dateFilterValue.value,
          fromDate: dateFilterValue.fromDate,
          toDate: dateFilterValue.toDate
        };
        break;
    }
    setDateValue(formattedDate);
    onTriggerDateFilterChange({
      data: formattedDate
    });
  };
  const generateCSSVariables = () => {
    const cssVariables = stack.reduce((variables, item) => {
      variables[`--kc-ui-${item.value}-color`] = item.color;
      return variables;
    }, {});
    return cssVariables;
  };
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: /*#__PURE__*/_jsx("div", {
      className: `kc-ui-colm kc-ui-stack ${disabled && "kc-ui-disabled "}`,
      style: {
        width: `${width}`
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: "kc-ui-colm-div",
        children: [title ? /*#__PURE__*/_jsx("h4", {
          children: /*#__PURE__*/_jsx(TranslateText, {
            id: title
          })
        }) : null, showHintMessage && /*#__PURE__*/_jsx("div", {
          className: "kc-ui-horizontalhintmessage-div",
          children: /*#__PURE__*/_jsx(HintMessage, {
            hintMessage: hintMessage
          })
        }), loader ? /*#__PURE__*/_jsx(Loader, {
          loader: loader
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("div", {
            className: "kc-ui-datefilter-div",
            children: dateFilter && /*#__PURE__*/_jsx(DateFilter, {
              title: dateFilterTitle,
              options: dateFilterOptions,
              value: dateValue,
              onTriggerChange: dateChange,
              dateFormat: dateFilterDateFormat
            })
          }), /*#__PURE__*/_jsxs("div", {
            children: [chartValue?.length ? /*#__PURE__*/_jsx(StackBarChart, {
              value: chartValue,
              stack: stackValue,
              slider: slider,
              filterValue: dateValue
            }) : null, !chartValue || !chartValue.length || typeof chartValue !== "object" && noValueText ? /*#__PURE__*/_jsx("p", {
              className: "kc-ui-align-no-result kc-ui-align-center",
              children: /*#__PURE__*/_jsx(TranslateText, {
                id: noValueText
              })
            }) : null]
          }), chartValue?.length && legend ? /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx("ul", {
              className: "kc-ui-legend",
              children: stackValue?.length ? stackValue.map((item, index) => {
                return /*#__PURE__*/_jsxs("li", {
                  style: generateCSSVariables(),
                  children: [/*#__PURE__*/_jsx("span", {
                    className: `kc-ui-${item.value}`
                  }, item.value), /*#__PURE__*/_jsx(TranslateText, {
                    id: item.label
                  })]
                }, index);
              }) : null
            })
          }) : null]
        })]
      })
    })
  });
};
_HorizontalStackBarChart.defaultProps = {
  title: "",
  dateFilterTitle: "",
  dateFilter: false,
  dateFilterOptions: [],
  onTriggerDateFilterChange: () => {},
  dateFilterValue: {},
  dateFilterDateFormat: "MM/dd/yyyy",
  loader: false,
  stack: [],
  value: [],
  legend: true,
  slider: true,
  width: "100%",
  disabled: false,
  noValueText: ''
};
export const HorizontalStackBarChartWidget = injectIntl( /*#__PURE__*/React.memo(_HorizontalStackBarChart));